import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, XCircle, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "checking" });
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState({ phase: "error", msg: "Missing payment session" }); return; }
    let timer;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setState({ phase: "paid", data }); return; }
        if (["expired", "failed"].includes(data.payment_status)) { setState({ phase: "failed", data }); return; }
        if (++tries.current >= 6) { setState({ phase: "timeout", data }); return; }
        timer = setTimeout(poll, 2000);
      } catch (e) {
        setState({ phase: "error", msg: e.response?.data?.detail || "Could not verify payment" });
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <main className="max-w-xl mx-auto px-4 sm:px-6 py-20 sm:py-28 text-center" data-testid="payment-success-page">
      {state.phase === "checking" && (<>
        <Loader2 className="h-10 w-10 animate-spin text-sky-700 mx-auto" />
        <h1 className="font-display text-2xl font-semibold text-slate-900 mt-6">Confirming your payment…</h1>
        <p className="text-sm text-slate-500 mt-2">Please keep this page open for a moment.</p>
      </>)}
      {state.phase === "paid" && (<>
        <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto" />
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 mt-6">Payment received</h1>
        <p className="text-base text-slate-600 mt-3">Order <span className="font-tech text-slate-900" data-testid="payment-success-order-id">{state.data.order_id}</span> is paid — ${Number(state.data.amount).toFixed(2)}. A receipt from Stripe is on its way to {state.data.email}.</p>
        <Link to={`/track?order_id=${state.data.order_id}&email=${encodeURIComponent(state.data.email)}`}>
          <Button data-testid="payment-success-track-btn" className="mt-8">Track this order</Button>
        </Link>
      </>)}
      {(state.phase === "timeout") && (<>
        <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto" />
        <h1 className="font-display text-2xl font-semibold text-slate-900 mt-6">Still confirming</h1>
        <p className="text-sm text-slate-600 mt-2">Stripe hasn't confirmed yet. Your order {state.data?.order_id} will update automatically — check the tracking page in a minute.</p>
        <Link to="/track"><Button variant="outline" className="mt-6">Go to tracking</Button></Link>
      </>)}
      {(state.phase === "failed" || state.phase === "error") && (<>
        <XCircle className="h-12 w-12 text-red-500 mx-auto" />
        <h1 className="font-display text-2xl font-semibold text-slate-900 mt-6">Payment not completed</h1>
        <p className="text-sm text-slate-600 mt-2">{state.msg || "The payment session expired or failed. You can retry from your order's pay page."}</p>
        <Link to="/track"><Button variant="outline" className="mt-6">Find my order</Button></Link>
      </>)}
    </main>
  );
}

export function PaymentCancel() {
  const [params] = useSearchParams();
  const orderId = params.get("order_id");
  const email = params.get("email");
  return (
    <main className="max-w-xl mx-auto px-4 sm:px-6 py-20 sm:py-28 text-center" data-testid="payment-cancel-page">
      <XCircle className="h-12 w-12 text-slate-400 mx-auto" />
      <h1 className="font-display text-3xl font-bold text-slate-900 mt-6">Payment cancelled</h1>
      <p className="text-base text-slate-600 mt-3">No charge was made. Your order request{orderId ? ` ${orderId}` : ""} is still on file — you can pay by card later or request an invoice.</p>
      {orderId && email && (
        <Link to={`/pay/${orderId}?email=${encodeURIComponent(email)}`}>
          <Button data-testid="payment-cancel-retry-btn" className="mt-8">Back to payment options</Button>
        </Link>
      )}
    </main>
  );
}
